import React, { useEffect, useRef, useState } from 'react';
import { useProfileParams, isAISummariesAccessible } from '../../../../../../utils/common';
import { useGetBusinessPerformanceDetailsQuery } from '../../../../../../redux/services/businessPerformaceAPI';
import { useGetAGCAIDetailsMutation } from '../../../../../../redux/services/agcAIAPI';
import KPITile from '../../../../../common/KPITile/KPITile';
import KPIDetailTile from '../../../../../common/KPIDetailTile/KPIDetailTile';
import DonutChart, { DonutChartSegment } from '../../../../../common/DonutChart/DonutChart';
import styles from './QMixTile.module.scss';
import InsightAI from '../../../../../common/InsightAI/InsightAI';
import { useTranslation } from 'react-i18next';
import ViewModeSwitcher, {
  ViewMode,
} from '../../../../../common/ViewModeSwitcher/ViewModeSwitcher';
import GenericTable from '../../../../../common/GenericTable/GenericTable';
import Skeleton from '../../../../../common/Skeleton/Skeleton';
import isEmpty from 'lodash/isEmpty';

interface SummaryProp {
  summary?: any;
  loading?: boolean;
}

export const QMixTile: React.FC<SummaryProp> = ({ summary, loading = false }) => {
  // Display API-provided segments when available; otherwise show nothing (0 fallback is implicit per segment values)
  const segments: DonutChartSegment[] = Array.isArray(summary?.segments) ? summary!.segments : [];
  const { t } = useTranslation();

  return (
    <KPITile
      title={t('qmixTitle')}
      dataTestId="qmix-tile"
      loading={loading}
      className={styles.center_content}
    >
      <div className={styles.tile_content}>
        <DonutChart size={125} strokeWidth={20} legendGap={48} segments={segments} showLegend />
      </div>
    </KPITile>
  );
};

export const QMixDetail: React.FC<{ kpiCode?: string; onClose?: () => void }> = ({
  kpiCode = 'qMix',
  onClose,
}) => {
  const { affiliateCode, aboNumber } = useProfileParams();
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>('left');

  const { data: kpiDetailsResponse, isLoading } = useGetBusinessPerformanceDetailsQuery(
    {
      affiliateCode,
      aboNumber,
      payload: { kpis: [{ code: kpiCode }] } as any,
    },
    { skip: !onClose }
  );

  const [getAGCAIDetails, { isLoading: isLoadingAiSummary }] = useGetAGCAIDetailsMutation();
  const [aiContent, setAiContent] = useState<string>('');
  const hasRequestedRef = useRef(false);

  useEffect(() => {
    // Reset ref when modal closes
    if (!onClose) {
      hasRequestedRef.current = false;
      setAiContent('');
      return;
    }

    const canFetch = Number(affiliateCode) !== 0 && aboNumber !== 0 && Boolean(onClose);
    if (!canFetch || hasRequestedRef.current) return;

    hasRequestedRef.current = true;
    getAGCAIDetails({ affiliateCode, aboNumber, payload: { kpis: [{ code: kpiCode }] } as any })
      .then((resp: any) => {
        // Handle different response structures
        const responseData = resp?.data || resp;
        const aiRaw = responseData?.body || responseData;

        // Try multiple extraction patterns
        const aiResolved =
          typeof aiRaw === 'string' ? aiRaw : aiRaw?.[kpiCode] || aiRaw?.summary || aiRaw || '';

        const content = aiResolved ? String(aiResolved).slice(0, 300).concat('...') : '';
        setAiContent(content);
      })
      .catch((error: any) => {
        console.error('Error fetching AI details:', error);
        setAiContent('');
      });
  }, [affiliateCode, aboNumber, onClose, kpiCode, getAGCAIDetails]);

  const detailData = kpiDetailsResponse?.body?.kpis?.[0] || {};
  const monthlyViewCharts = detailData?.detail?.monthlyView?.chart || [];

  // Extract bar chart data and transform to donut chart segments
  const barChart = monthlyViewCharts.find((chart: any) => chart.chartType === 'barChart');
  const barChartDetail = barChart?.chartDetail?.barChartDetail;

  // Transform bar chart data to donut chart format
  const donutChartData: { title: string; segments: DonutChartSegment[] }[] = barChartDetail?.body
    ?.bars
    ? barChartDetail.body.bars.map((bar: any) => {
        const segments: DonutChartSegment[] = bar.barComponent
          ? bar.barComponent.map((component: any) => ({
              label:
                component.xValue === 'qv'
                  ? 'QV'
                  : component.xValue === 'q1'
                    ? 'Q1'
                    : component.xValue === 'q2'
                      ? 'Q2'
                      : 'Other',
              value: parseFloat(component.yValue) || 0,
            }))
          : [];
        // Transform barName (e.g., "fy24" -> "FY 2024", "fy25" -> "FY 2025")
        const getTitle = (barName: string): string => {
          const match = barName.match(/fy(\d{2})/i);
          if (match) {
            const fullYear = `20${match[1]}`;
            return `FY ${fullYear}`;
          }
          // Fallback: capitalize the barName if it doesn't match the pattern
          return barName.toUpperCase();
        };
        const title = bar.barName ? getTitle(bar.barName) : '';
        return {
          title,
          segments,
        };
      })
    : [];

  const hasDonutChartData =
    donutChartData.length > 0 && donutChartData.some((item) => item.segments.length > 0);

  // Extract table data
  const tableChart = monthlyViewCharts.find((chart: any) => chart.chartType === 'table');
  const tableData = tableChart?.chartDetail?.tableDetail;

  useEffect(() => {
    if (!hasDonutChartData) {
      setViewMode('right');
    }
  }, [hasDonutChartData]);

  if (isLoading) return <Skeleton height="400px" />;

  const headerContent = (
    <div className={styles.header_content}>
      <div className={styles.tile_header}>
        <div className={styles.title}>{t('qmixTitle')}</div>
        <div className={styles.desc}>{t('qmix.description')}</div>
      </div>
      <div className={styles.chart_view_wrapper}>
        <ViewModeSwitcher
          mode={viewMode}
          onChangeMode={setViewMode}
          disabled={false}
          showSwitch={hasDonutChartData}
        />
      </div>
    </div>
  );

  const leftContent =
    viewMode === 'left' ? (
      hasDonutChartData ? (
        <div className={styles.donuts_card}>
          {donutChartData.map((item, idx) => (
            <React.Fragment key={idx}>
              <div className={styles.donut_section}>
                <div className={styles.donut_title}>{item.title}</div>
                <DonutChart
                  size={90}
                  strokeWidth={17}
                  legendGap={16}
                  segments={item.segments}
                  showLegend
                />
              </div>
              {idx < donutChartData.length - 1 && <div className={styles.divider} />}
            </React.Fragment>
          ))}
        </div>
      ) : (
        <div className={styles.no_data}>{t('noDataAvailable')}</div>
      )
    ) : isEmpty(tableData) ? (
      <div className={styles.no_data}>{t('noDataAvailable')}</div>
    ) : (
      <GenericTable tableData={tableData} kpiCode={kpiCode} />
    );

  const rightContent = isAISummariesAccessible() ? (
    isLoadingAiSummary ? (
      <Skeleton height="200px" />
    ) : (
      <InsightAI
        updateDate=""
        content={aiContent}
        title={t('ai_summary')}
        showDropdown={false}
        isDropdownEnabled={true}
        flowerIconSize={24}
        titleSize={14}
      />
    )
  ) : null;

  return (
    <KPIDetailTile
      headerContent={headerContent}
      leftContent={leftContent}
      rightContent={rightContent}
      onClose={onClose!}
      dataTestId="qmix-detail"
    />
  );
};

export default QMixTile;
